import React, { useState, useEffect } from 'react';
import { Plus, AlertTriangle, CheckCircle, Clock, Edit, Trash2, Award, Calendar, ExternalLink } from 'lucide-react';
import { staffAPI } from '../services/api';
import AdvancedFilters from '../components/UI/AdvancedFilters';
import SelfServiceListSkeleton from '../components/UI/SelfServiceListSkeleton';
import Modal from '../components/UI/Modal';
import toast from '../utils/toast';
import ConfirmModal from '../components/UI/ConfirmModal';
import { StatusBadge } from '../components/UI/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import FormSelect from '../components/UI/FormSelect';

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [expiringCertificates, setExpiringCertificates] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    certificate_name: '',
    issuing_organization: '',
    issue_date: '',
    expiry_date: '',
    category: '',
    certificate_number: '',
    description: '',
    verification_status: 'pending',
    renewal_required: false,
    renewal_reminder_days: 30
  });

  // Categories for filtering
  const categories = [
    { value: 'technical', label: 'Technical' },
    { value: 'safety', label: 'Safety' },
    { value: 'professional', label: 'Professional' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'industry', label: 'Industry Specific' },
    { value: 'language', label: 'Language' },
    { value: 'management', label: 'Management' },
    { value: 'other', label: 'Other' }
  ];

  const verificationStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' }
  ];

  // Filter configuration
  const filterConfig = {
    searchFields: ['certificate_name', 'issuing_organization', 'certificate_number'],
    choiceFilters: [
      { key: 'category', label: 'Category', options: categories },
      { key: 'verification_status', label: 'Verification Status', options: verificationStatuses }
    ],
    dateFilters: [
      { key: 'issue_date', label: 'Issue Date Range' },
      { key: 'expiry_date', label: 'Expiry Date Range' }
    ],
    booleanFilters: [
      { key: 'renewal_required', label: 'Renewal Required' },
      { key: 'is_expired', label: 'Show Expired Only' },
      { key: 'expiring_soon', label: 'Expiring Soon (30 days)' }
    ]
  };

  useEffect(() => {
    fetchCertificates();
    fetchStatistics();
    fetchExpiringCertificates();
  }, []);

  const fetchCertificates = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await staffAPI.getCertificates(filters);
      setCertificates(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to fetch certificates');
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await staffAPI.getCertificateStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchExpiringCertificates = async () => {
    try {
      const response = await staffAPI.getExpiringCertificates();
      setExpiringCertificates(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching expiring certificates:', error);
    }
  };

  const handleFilterChange = (filters) => {
    fetchCertificates(filters);
  };

  const handleAddCertificate = async (e) => {
    e.preventDefault();
    try {
      const response = await staffAPI.createCertificate(formData);
      toast.success('Certificate added successfully');
      setShowAddForm(false);
      resetForm();
      fetchCertificates();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to add certificate');
      console.error('Error adding certificate:', error);
    }
  };

  const handleEditCertificate = async (e) => {
    e.preventDefault();
    try {
      const response = await staffAPI.updateCertificate(editingCertificate.id, formData);
      toast.success('Certificate updated successfully');
      setEditingCertificate(null);
      resetForm();
      fetchCertificates();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to update certificate');
      console.error('Error updating certificate:', error);
    }
  };

  const handleDeleteCertificate = (id) => {
    setConfirmAction({ id });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await staffAPI.deleteCertificate(confirmAction.id);
      toast.success('Certificate deleted successfully');
      fetchCertificates();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to delete certificate');
      console.error('Error deleting certificate:', error);
    } finally {
      setConfirmAction(null);
    }
  };

  const resetForm = () => {
    setFormData({
      certificate_name: '',
      issuing_organization: '',
      issue_date: '',
      expiry_date: '',
      category: '',
      certificate_number: '',
      description: '',
      verification_status: 'pending',
      renewal_required: false,
      renewal_reminder_days: 30
    });
  };

  const startEdit = (certificate) => {
    setEditingCertificate(certificate);
    setFormData({
      certificate_name: certificate.certificate_name || '',
      issuing_organization: certificate.issuing_organization || '',
      issue_date: certificate.issue_date || '',
      expiry_date: certificate.expiry_date || '',
      category: certificate.category || '',
      certificate_number: certificate.certificate_number || '',
      description: certificate.description || '',
      verification_status: certificate.verification_status || 'pending',
      renewal_required: certificate.renewal_required || false,
      renewal_reminder_days: certificate.renewal_reminder_days || 30
    });
    setShowAddForm(true);
  };

  const getCategoryColor = (category) => {
    const colors = {
      technical: 'bg-blue-100 text-blue-800',
      safety: 'bg-red-100 text-red-800',
      professional: 'bg-purple-100 text-purple-800',
      compliance: 'bg-green-100 text-green-800',
      industry: 'bg-orange-100 text-orange-800',
      language: 'bg-pink-100 text-pink-800',
      management: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysToExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysToExpiry <= 30 && daysToExpiry > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  const StatisticsCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SelfServiceListSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Professional Certificates</h1>
        <p className="text-gray-600">Manage your professional certifications and track renewal requirements</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <StatisticsCard
          title="Total Certificates"
          value={statistics.total_certificates || 0}
          icon={Award}
          color="blue"
        />
        <StatisticsCard
          title="Verified"
          value={statistics.verified_certificates || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatisticsCard
          title="Expiring Soon"
          value={statistics.expiring_soon || 0}
          icon={Clock}
          color="yellow"
        />
        <StatisticsCard
          title="Expired"
          value={statistics.expired_certificates || 0}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Expiring Soon Alert */}
      {expiringCertificates.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <h3 className="text-lg font-semibold text-yellow-800">Renewal Alerts</h3>
          </div>
          <p className="text-yellow-700 mt-2">
            {expiringCertificates.length} certificate{expiringCertificates.length > 1 ? 's' : ''} expiring within 30 days
          </p>
          <div className="mt-3 space-y-2">
            {expiringCertificates.slice(0, 3).map(cert => (
              <div key={cert.id} className="flex items-center justify-between text-sm">
                <span className="font-medium">{cert.certificate_name}</span>
                <span className="text-yellow-600">Expires: {formatDate(cert.expiry_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Advanced Filters */}
      <div className="mb-6">
        <AdvancedFilters
          config={filterConfig}
          onFilterChange={handleFilterChange}
          resultCount={certificates.length}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Certificate
        </button>
      </div>

      {/* Certificates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certificates.map(certificate => (
          <div key={certificate.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {certificate.certificate_name}
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  {certificate.issuing_organization}
                </p>
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(certificate.category)}`}>
                    {certificate.category}
                  </span>
                  <StatusBadge
                    status={certificate.verification_status === 'expired' ? 'deactivated' : certificate.verification_status}
                    label={certificate.verification_status === 'expired' ? 'Expired' : undefined}
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(certificate)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteCertificate(certificate.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                <span>Issue: {formatDate(certificate.issue_date)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                <span className={`${isExpired(certificate.expiry_date) ? 'text-red-600' : isExpiringSoon(certificate.expiry_date) ? 'text-yellow-600' : 'text-gray-600'}`}>
                  Expires: {formatDate(certificate.expiry_date)}
                </span>
              </div>
              {certificate.certificate_number && (
                <div className="flex items-center">
                  <ExternalLink className="w-4 h-4 mr-2 text-gray-400" />
                  <span>#{certificate.certificate_number}</span>
                </div>
              )}
            </div>

            {certificate.description && (
              <p className="text-sm text-gray-600 mt-3 border-t pt-3">
                {certificate.description}
              </p>
            )}

            {certificate.renewal_required && (
              <div className="mt-3 p-2 bg-yellow-50 rounded text-sm text-yellow-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Renewal required
              </div>
            )}
          </div>
        ))}
      </div>

      {certificates.length === 0 && (
        <div className="text-center py-12">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No certificates found</h3>
          <p className="text-gray-600">Add your first certificate to get started</p>
        </div>
      )}

      {/* Add/Edit Certificate Modal */}
      <Modal
        isOpen={showAddForm}
        onClose={() => { setShowAddForm(false); setEditingCertificate(null); resetForm(); }}
        title={editingCertificate ? 'Edit Certificate' : 'Add Certificate'}
        size="md"
        theme="employee"
      >
            <form onSubmit={editingCertificate ? handleEditCertificate : handleAddCertificate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certificate Name *
                  </label>
                  <input
                    type="text"
                    value={formData.certificate_name}
                    onChange={(e) => setFormData({...formData, certificate_name: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issuing Organization *
                  </label>
                  <input
                    type="text"
                    value={formData.issuing_organization}
                    onChange={(e) => setFormData({...formData, issuing_organization: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Date *
                  </label>
                  <input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <FormSelect
                    options={[
                      { value: '', label: 'Select category' },
                      ...categories.map(cat => ({ value: cat.value, label: cat.label }))
                    ]}
                    value={formData.category}
                    onChange={(val) => setFormData({...formData, category: val})}
                    theme="employee"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certificate Number
                  </label>
                  <input
                    type="text"
                    value={formData.certificate_number}
                    onChange={(e) => setFormData({...formData, certificate_number: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Status
                  </label>
                  <FormSelect
                    options={verificationStatuses.map(status => ({ value: status.value, label: status.label }))}
                    value={formData.verification_status}
                    onChange={(val) => setFormData({...formData, verification_status: val})}
                    theme="employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renewal Reminder (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.renewal_reminder_days}
                    onChange={(e) => setFormData({...formData, renewal_reminder_days: parseInt(e.target.value)})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    min="1"
                    max="365"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.renewal_required}
                      onChange={(e) => setFormData({...formData, renewal_required: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Renewal Required</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingCertificate(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCertificate ? 'Update' : 'Add'} Certificate
                </button>
              </div>
            </form>
      </Modal>

      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Certificate"
        message="Are you sure you want to delete this certificate?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="employee"
      />
    </div>
  );
};

export default Certificates; 